import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import Dashboard from './pages/Dashboard';
import { OnboardingWizard } from './pages/Onboarding';
import EnhancedOnboardingFlow from './pages/Onboarding/EnhancedOnboardingFlow';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import AscinLoginPage from './pages/Auth/AscinLoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/Layout/AppLayout';
import { AuthProvider } from './context/AuthContext';
import { FounderModeProvider } from './components/dope';
import AgentsPage from './pages/AgentsPage';
import SmartOnboardingPage from './pages/SmartOnboardingPage';
import TaskListPage from './pages/HITL/TaskListPage';
import TaskDetailPage from './pages/HITL/TaskDetailPage';
import AgentsDashboard from './pages/Agents/AgentsDashboard';
import ProductsPage from './pages/SupplyChain/ProductsPage';
import SuppliersPage from './pages/SupplyChain/SuppliersPage';
import InventoryPage from './pages/SupplyChain/InventoryPage';
import LocationsPage from './pages/SupplyChain/LocationsPage';
import ProcurementPage from './pages/SupplyChain/ProcurementPage';
import ReceivingPage from './pages/SupplyChain/ReceivingPage';
import ReturnsPage from './pages/SupplyChain/ReturnsPage';
import ReorderingPage from './pages/SupplyChain/ReorderingPage';
import ComponentsPage from './pages/SupplyChain/ComponentsPage';
import WarehousesPage from './pages/SupplyChain/WarehousesPage';
import AnalyticsPage from './pages/Analytics/AnalyticsPage';
import SimulationDashboard from './pages/Simulation/SimulationDashboard';
import SimulationTemplates from './pages/Simulation/SimulationTemplates';
import ChatPage from './pages/Chat/ChatPage';
import SettingsPage from './pages/Settings/SettingsPage';
import CompanyProfilePage from './pages/Settings/CompanyProfilePage';
import ContactsPage from './pages/CRM/ContactsPage';
import DealsPage from './pages/CRM/DealsPage';
import NegotiationsPage from './pages/Negotiations/NegotiationsPage';
import HITLNegotiationPage from './pages/Negotiations/HITLNegotiationPage';
import HelpPage from './pages/HelpPage';
import DataImportPage from './pages/DataImport/DataImportPage';
import IntegrationsPage from './pages/Integrations/IntegrationsPage';
import MigrationWizardPage from './pages/Migration/MigrationWizardPage';
import EFLinkLanding from './pages/LandingPage';
import MESHDashboard from './pages/MESH/MESHDashboard';

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#00796b',
      dark: '#004d40',
      light: '#b2dfdb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff9800',
      dark: '#f57c00',
      light: '#ffb74d',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 500,
    },
    h2: {
      fontWeight: 500,
    },
    h3: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FounderModeProvider>
        <AuthProvider>
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<EFLinkLanding />} />
              <Route path="/landing" element={<EFLinkLanding />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/ascin-login" element={<AscinLoginPage />} />
              <Route path="/onboarding" element={<EnhancedOnboardingFlow />} />

              {/* Protected Routes with Layout */}
              <Route
                path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ChatPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AgentsDashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TaskListPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/:id"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TaskDetailPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/supply-chain"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProductsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AnalyticsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/simulation"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SimulationDashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/simulation/templates"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SimulationTemplates />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/suppliers"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SuppliersPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <InventoryPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/locations"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <LocationsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProcurementPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/receiving"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ReceivingPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/returns"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ReturnsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reordering"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ReorderingPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/components"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ComponentsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/warehouses"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <WarehousesPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ChatPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SettingsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/company-profile"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <CompanyProfilePage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/contacts"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ContactsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/deals"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DealsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/negotiations"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <NegotiationsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/negotiations/hitl"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <HITLNegotiationPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <HelpPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/data-import"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DataImportPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <IntegrationsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/migration"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <MigrationWizardPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/mesh"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <MESHDashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Legacy Routes */}
            <Route path="/onboarding-old/*" element={<OnboardingWizard />} />
            <Route path="/smart-onboarding" element={<SmartOnboardingPage />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </AuthProvider>
      </FounderModeProvider>
    </ThemeProvider>
  );
};

export default App;
